/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";
import { useSelector } from "react-redux";

// react-flatpickr components
import Flatpickr from "react-flatpickr";

// react-flatpickr styles
import "flatpickr/dist/flatpickr.css";

// flatpickr locales
import { Spanish } from "flatpickr/dist/l10n/es.js";
import { english } from "flatpickr/dist/l10n/default.js";
import { Mandarin } from "flatpickr/dist/l10n/zh.js";
import { French } from "flatpickr/dist/l10n/fr.js";
import { Italian } from "flatpickr/dist/l10n/it.js";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

// Material Dashboard 2 PRO React TS components
import MDInput from "dashboard-template/components/MDInput";

// Redux
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import { selectCurrentUser } from "store/slices/auth.slice";

// types
export interface Props {
  input?: {
    [key: string]: any;
  };
  locale?: any; // Allow manual locale override
  [key: string]: any;
}

/**
 * Maps language codes to Flatpickr locale objects
 */
const localeMap: Record<string, any> = {
  en: english,
  es: Spanish,
  zh: Mandarin,
  fr: French,
  it: Italian,
  pt: Portuguese,
};

function MDDatePicker({
  input,
  locale,
  ...rest
}: Props): JSX.Element {
  // Get language preferences from Redux store
  const currentUser = useSelector(selectCurrentUser);
  const generalSettings = useSelector(selectGeneralSettings);

  // Determine the locale to use with priority:
  // 1. Manual locale prop
  // 2. User profile language preference
  // 3. General settings language
  // 4. Spanish as default
  const effectiveLocale = useMemo(() => {
    if (locale) {
      return locale;
    }

    // Try user profile language first
    const userLanguage = currentUser?.profile?.language;
    if (userLanguage && localeMap[userLanguage]) {
      return localeMap[userLanguage];
    }

    // Fallback to general settings language
    const systemLanguage = generalSettings?.language;
    if (systemLanguage && localeMap[systemLanguage]) {
      return localeMap[systemLanguage];
    }

    // Default to Spanish
    return Spanish;
  }, [locale, currentUser?.profile?.language, generalSettings?.language]);

  return (
    <Flatpickr
      {...rest}
      options={{ locale: effectiveLocale, ...rest.options }}
      render={({ defaultValue }: any, ref: any) => (
        <MDInput {...input} defaultValue={defaultValue} inputRef={ref} />
      )}
    />
  );
}

export default MDDatePicker;
